import { CustomButton, ButtonStats } from '../../types';
import { API_BASE_URL } from './types';

/**
 * Fetch buttons for a specific counter
 */
export async function fetchButtons(counterId?: string, isOwnerUrl = false): Promise<CustomButton[]> {
  if (!counterId) {
    throw new Error('Counter ID is required');
  }
  
  const url = `${API_BASE_URL}/api/counters/${counterId}/buttons${isOwnerUrl ? '?isOwnerUrl=true' : ''}`;
  
  try {
    const response = await fetch(url);
    
    if (response.status === 404) {
      // Counter has been deleted - handle gracefully with an empty array
      return [];
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch buttons');
    }
    
    const data = await response.json();
    
    // Sort buttons by creation date (oldest first)
    return data.sort((a: CustomButton, b: CustomButton) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  } catch (error) {
    // Rethrow for proper handling by caller
    throw error instanceof Error ? error : new Error('Failed to fetch buttons');
  }
}

/**
 * Fetch button statistics for a specific counter
 */
export async function fetchButtonStats(counterId: string, isOwnerUrl = false): Promise<ButtonStats[]> {
  if (!counterId) {
    throw new Error('Counter ID is required');
  }
  
  const url = `${API_BASE_URL}/api/counters/${counterId}/buttons/stats${isOwnerUrl ? '?isOwnerUrl=true' : ''}`;
  
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch stats');
  return await response.json();
}

/**
 * Increment a button's count
 */
export async function incrementButton(counterId: string, buttonId: string, isOwnerUrl = false): Promise<CustomButton> {
  const url = `${API_BASE_URL}/api/counters/${counterId}/buttons/${buttonId}/increment${isOwnerUrl ? '?isOwnerUrl=true' : ''}`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to increment button');
  }
  
  return await response.json();
}

/**
 * Create a new button
 */
export async function createButton(
  counterId: string,
  buttonData: { name: string, color: string, icon: string, costToClick?: number },
  isOwnerUrl = false
): Promise<CustomButton> {
  const url = `${API_BASE_URL}/api/counters/${counterId}/buttons${isOwnerUrl ? '?isOwnerUrl=true' : ''}`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(buttonData)
  });
  
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to create button');
  }
  
  return await response.json();
}

/**
 * Update a button
 */
export async function updateButton(
  counterId: string,
  buttonId: string, 
  buttonData: { name: string, color: string, icon: string, costToClick?: number },
  isOwnerUrl = false
): Promise<CustomButton> {
  const url = `${API_BASE_URL}/api/counters/${counterId}/buttons/${buttonId}${isOwnerUrl ? '?isOwnerUrl=true' : ''}`;
  
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(buttonData)
  });
  
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to update button');
  }
  
  return await response.json();
}

/**
 * Delete a button
 */
export async function deleteButton(
  counterId: string,
  buttonId: string,
  isOwnerUrl = false
): Promise<{ message: string, deletedButton: { id: string, name: string } }> {
  const url = `${API_BASE_URL}/api/counters/${counterId}/buttons/${buttonId}${isOwnerUrl ? '?isOwnerUrl=true' : ''}`;
  
  const response = await fetch(url, {
    method: 'DELETE'
  });
  
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to delete button');
  }
  
  return await response.json();
}

/**
 * Reset all button counts
 */
export async function resetButtonCounts(counterId: string, isOwnerUrl = false): Promise<CustomButton[]> {
  const url = `${API_BASE_URL}/api/counters/${counterId}/buttons/reset${isOwnerUrl ? '?isOwnerUrl=true' : ''}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) throw new Error('Failed to reset button counts');
  return await response.json();
}

/**
 * Delete all buttons
 */
export async function deleteAllButtons(counterId: string, isOwnerUrl = false): Promise<{ message: string }> {
  const url = `${API_BASE_URL}/api/counters/${counterId}/buttons${isOwnerUrl ? '?isOwnerUrl=true' : ''}`;

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) throw new Error('Failed to delete all buttons');
  return await response.json();
} 